import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { fetchPollingSuccess } from "store/actions/polling";
import { setAnomalyAction } from "store/actions/smartInsights";
import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";
import useSystemsDataHandler from "hooks/useSystemsDataHandler";

import { fetchAnomalyList, fetchSystemsAndAlarms } from "api/landing";
import { addSystem, editSystem } from "api/systems.js";

import { getBaselinePagePath } from "utils/router.utils";
import { dialogStrings, InputErrorStrings, SYSTEM_TABLE_COLUMNS, SYSTEM_UNITS, userProfileStrings, valueStrings } from "constants/constantStrings";
import { INPUT_ADDRESS_MAXIMUM, INPUT_DESIGN_MAXIMUM, INPUT_MINIMUM, INPUT_SIZE_MAXIMUM } from "constants/globalConstants";

import Dropdown from "components/Dropdown";
import DupontDialog from "components/DupontDialog";
import InputBox from "components/InputBox";
import { TextFieldValidator } from "components/InputBox/TextFieldValidator";

import { COUNTRIES_WITH_CODE, SYSTEM_FIELDS } from "./SystemForm.constant";

export default function SystemForm(props) {
  const {
    setIsSystemFormOpen,
    type,
    system,
    setSystem,
    systems,
    setSystems,
    systemNames,
    setSystemNames,
    systemIDs,
    setSystemIDs,
    editSystemIndex,
    setBaselineDialog,
    baselineDialog
  } = props;
  const [systemIdError, setSystemIdError] = useState("");
  const [systemNameError, setSystemNameError] = useState("");
  const [confirmDisable, setconfirmDisable] = useState(true);
  const [isLoading, setisLoading] = useState(false);
  const objAppInsights = useAppInsightsHandler();
  const [addedSystemId, setAddedSystemId] = useState(null);
  const { loadSystemsData } = useSystemsDataHandler();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const validateSystemId = value => {
    let error = "";
    if (!value) {
      error = InputErrorStrings.required;
    } else {
      if (systemIDs.includes(value.toLowerCase())) {
        error = userProfileStrings.uniqueSystemId;
      } else if (/\s/.test(value)) {
        error = userProfileStrings.invalidSystemId;
      } else if (value.length < INPUT_MINIMUM) {
        error = InputErrorStrings.minimumRequired;
      }
    }
    let systemData = system;
    if (type === "Edit") {
      if (value !== system.initialSystemID) {
        systemData = { ...system, SysId: value, NewSysId: value };
      } else {
        systemData = { ...system, SysId: value };
      }
      setSystem(systemData);
    }
    setSystemIdError(error);
  };

  const systemNameMaxLength = () => {
    if(systemNameError === ""){
    setSystemNameError(InputErrorStrings.maxlimitreached);
    }
  };

  const systemIdMaxLength = () => {
    if(systemIdError === ""){
    setSystemIdError(InputErrorStrings.maxlimitreached);
    }
  };

  const validateSystemName = value => {
    const formData = { ...system, SystemName: value };
    let error = "";
    if (!value) {
      error = InputErrorStrings.required;
    } else {
      if (systemNames.includes(value.toLowerCase())) {
        error = userProfileStrings.uniqueSystemName;
      } else if (value.trim().length < INPUT_MINIMUM) {
        error = InputErrorStrings.minimumRequired;
      }
    }
    setSystemNameError(error);
    setSystem(formData);
  };

  const handleInputChange = (value, name) => {
    if (name === SYSTEM_FIELDS.SystemID) {
      validateSystemId(value);
      setSystem({ ...system, SysId: value });
    } else {
      if (name === SYSTEM_FIELDS.SystemName) {
        validateSystemName(value);
      }
      const fieldVal = value || undefined;
      const sysData = { ...system, [name]: fieldVal };
      setSystem(sysData);
    }
  };

  const handleRadioChange = event => {
    const { name, value } = event.target;
    const formData = { ...system, [name]: value === "true" };
    setSystem(formData);
  };

  const handledropdownChange = (value, name) => {
    const systemData = { ...system, [name]: value || undefined };
    setSystem(systemData);
  };
  const navigateBaseline = () => {
    const path = getBaselinePagePath(addedSystemId);
    navigate(path);
    setBaselineDialog(false);
  };

  const handleCloseBaselineDialog = data => {
    if (data === dialogStrings.confirm) {
      navigateBaseline();
    } else if (data === dialogStrings.cancel) {
      setIsSystemFormOpen(null);
      setBaselineDialog(false);
    }
  };
  const handleCloseSystemForm = async data => {
    if (data === dialogStrings.confirm) {
      setconfirmDisable(true);
      setisLoading(true);
      try {
        if (type === dialogStrings.add) {
          await submitNewSystem();
          setBaselineDialog(true);
        } else {
          await submitEditSystem();
        }
      } catch (error) {
        console.error("Error during form submission:", error);
      }
    } else if (data === dialogStrings.cancel) {
      setSystemNameError("");
      setSystemIdError("");
      setIsSystemFormOpen(null);
    }
  };

  const validateForm = formData => {
    const SystemIdError =
      systemIdError === userProfileStrings.uniqueSystemId ||
      systemIdError === InputErrorStrings.required ||
      systemIdError === userProfileStrings.invalidSystemId;
    const SystemNameError = systemNameError === userProfileStrings.uniqueSystemName || systemNameError === InputErrorStrings.required;
    const invalidSystem = Boolean(
      !formData.SystemName ||
        formData.SystemName.trim().length < 3 ||
        !formData.SysId ||
        formData.SysId.trim().length < 3 ||
        /\s/.test(formData.SysId) ||
        SystemIdError ||
        SystemNameError
    );
    setconfirmDisable(invalidSystem);
  };
  useEffect(() => {
    validateForm(system);
  }, [system, systemIdError, systemNameError]);

  const submitNewSystem = async () => {
    objAppInsights.trackEvent("addNewSystem");
    try {
      const addedSystem = await addSystem(system);
      await loadSystemsData();
      setAddedSystemId(addedSystem.SysId);
      const systemsUpdated = [...systems];
      systemsUpdated.push(addedSystem);
      setSystems(systemsUpdated);
      const systemIDsUpdated = [...systemIDs];
      systemIDsUpdated.push(system.SysId.toLowerCase());
      setSystemIDs(systemIDsUpdated);
      const systemNamesUpdated = [...systemNames];
      systemNamesUpdated.push(system.SystemName.toLowerCase());
      setSystemNames(systemNamesUpdated);
      setconfirmDisable(false);
      setisLoading(false);
    } catch {
      setisLoading(false);
    }
  };
  const getChangedObject = (arr, obj) => {
    arr.forEach(key => delete obj[key]);
    return obj;
  };

  const updateALarm = async () => {
    const { systems, alarms } = await fetchSystemsAndAlarms();
    dispatch(fetchPollingSuccess(alarms, systems));
  };
  const updateInsights = async () => {
    const anomalyData = await fetchAnomalyList();
    dispatch(setAnomalyAction(anomalyData.data));
  };

  const submitEditSystem = async () => {
    objAppInsights.trackEvent("editSystem");
    const initialSystem = systems.find(selectedsystem => selectedsystem.SysId === system.SysId);

    if (JSON.stringify(system) !== JSON.stringify(initialSystem)) {
      try {
        const unchangedKeys = Object.keys(initialSystem).filter(k => initialSystem[k] === system[k]);
        const { SysId } = system;
        const systemChanges = getChangedObject(unchangedKeys, system);
        const respData = await editSystem({ ...systemChanges, SysId });
        const sysIdx = systems.findIndex(sys => sys.SysId === SysId);
        const updatedSystems = [...systems];
        updatedSystems[sysIdx] = respData;
        setSystems(updatedSystems);
        if (system[SYSTEM_TABLE_COLUMNS.enabled] !== undefined) {
          updateALarm();
          updateInsights();
        }
        await loadSystemsData();
        setIsSystemFormOpen(null);
        setconfirmDisable(false);
      } catch (err) {
        setconfirmDisable(false);
      } finally {
        setisLoading(false);
      }
    }
    setisLoading(false);
  };

  return (
    <>
      {system && (
        <DupontDialog
          open
          title={type === "Edit" ? userProfileStrings.editsystem : userProfileStrings.addaSystem}
          subTitle={type === "Edit" ? userProfileStrings.editaSystemMessage : userProfileStrings.addaSystemMessage}
          showisRequired
          cancelBtnText={valueStrings.cancel}
          confirmBtnText={type === "Add" ? valueStrings.add : valueStrings.save}
          onClose={handleCloseSystemForm}
          confirmDisabled={confirmDisable}
          customClass="system-form--dialog"
          isOverlayLoader={isLoading}
          disableBackdropClick
        >
          <div id="system-form-container">
            <div className="column padding-right-14">
              <InputBox
                customClass="position-relative"
                disabled={type === "Edit"}
                labelText={`${userProfileStrings.systemid}*`}
                name={SYSTEM_FIELDS.SystemID}
                placeholder={userProfileStrings.systemIdPlaceholder}
                value={system.SysId}
                maxLength={INPUT_ADDRESS_MAXIMUM}
                onChange={value => handleInputChange(value, SYSTEM_FIELDS.SystemID)}
                errorMsg={systemIdError}
                onMaxLengthReached={systemIdMaxLength}
                validator={TextFieldValidator.ALPHANUMERIC_WITH_FEW_SPECIAL_CHARS}
              />
              <InputBox
                customClass="margin-top-20"
                labelText={userProfileStrings.size}
                name={SYSTEM_FIELDS.Size}
                value={system.Size}
                maxLength={INPUT_SIZE_MAXIMUM}
                placeholder={valueStrings.type}
                onChange={value => handleInputChange(value, SYSTEM_FIELDS.Size)}
              />

              <div className="bodyMainHeadingText margin-top-15 ">
                <div className="column padding-left-0">
                  <label>{userProfileStrings.preTreatment}</label>
                  <div onChange={handleRadioChange}>
                    <label className="radiocontainer descriptionText">
                      {valueStrings.yes}
                      <input
                        className="radioLabel"
                        defaultChecked={system.PreTreatment}
                        type="radio"
                        value="true"
                        name={SYSTEM_FIELDS.PreTreatment}
                      />
                      <span className="radioButton"> </span>
                    </label>
                    <label className="radiocontainer descriptionText">
                      {valueStrings.no}
                      <input
                        className="radioLabel"
                        type="radio"
                        defaultChecked={!system.PreTreatment}
                        value="false"
                        name={SYSTEM_FIELDS.PreTreatment}
                      />
                      <span className="radioButton"> </span>
                    </label>
                  </div>
                </div>
                <div className="column  padding-left-0">
                  <label>{userProfileStrings.postTreatment}</label>
                  <div onChange={handleRadioChange}>
                    <label className="radiocontainer descriptionText">
                      {valueStrings.yes}
                      <input
                        className="radioLabel"
                        type="radio"
                        defaultChecked={system.PostTreatment}
                        value="true"
                        name={SYSTEM_FIELDS.PostTreatment}
                      />
                      <span className="radioButton"> </span>
                    </label>
                    <label className=" radiocontainer descriptionText">
                      {valueStrings.no}
                      <input
                        className="radioLabel"
                        type="radio"
                        defaultChecked={!system.PostTreatment}
                        value="false"
                        name={SYSTEM_FIELDS.PostTreatment}
                      />
                      <span className="radioButton"> </span>
                    </label>
                  </div>
                </div>
              </div>

              <Dropdown
                customClass="margin-top-120"
                labelText={`${userProfileStrings.country}`}
                value={system.Location}
                name={SYSTEM_FIELDS.SystemLocation}
                options={Object.keys(COUNTRIES_WITH_CODE)}
                getOptionLabel={option => COUNTRIES_WITH_CODE[option]}
                onChange={value => handledropdownChange(value, SYSTEM_FIELDS.SystemLocation)}
                placeholder={valueStrings.chooseOptionStr}
              />

              <Dropdown
                customClass="margin-top-20"
                labelText={`${userProfileStrings.units}`}
                value={system.Units}
                name={SYSTEM_FIELDS.SystemUnits}
                options={Object.keys(SYSTEM_UNITS)}
                onChange={value => handledropdownChange(value, SYSTEM_FIELDS.SystemUnits)}
                placeholder={valueStrings.chooseOptionStr}
              />
            </div>

            <div className="column">
              <InputBox
                customClass="position-relative"
                labelText={`${userProfileStrings.systemName}*`}
                name={SYSTEM_FIELDS.SystemName}
                placeholder={userProfileStrings.systemNamePlaceholder}
                value={system.SystemName}
                maxLength={INPUT_ADDRESS_MAXIMUM}
                onChange={value => handleInputChange(value, SYSTEM_FIELDS.SystemName)}
                errorMsg={systemNameError}
                onMaxLengthReached={systemNameMaxLength}
              />
              <InputBox
                customClass="margin-top-20"
                labelText={userProfileStrings.designFlow.replace("%", SYSTEM_UNITS[system.Units || userProfileStrings.us])}
                name={SYSTEM_FIELDS.DesignFlow}
                value={system.DesignFlow}
                placeholder={valueStrings.type}
                maxLength={INPUT_DESIGN_MAXIMUM}
                onChange={value => handleInputChange(value, SYSTEM_FIELDS.DesignFlow)}
              />
              <div className="bodyMainHeadingText margin-top-15 ">
                <div className="column">
                  <label>{userProfileStrings.max}</label>
                  <div onChange={handleRadioChange}>
                    <label className="radiocontainer descriptionText">
                      {valueStrings.yes}
                      <input className="radioLabel" type="radio" defaultChecked={system.Max} value="true" name={SYSTEM_FIELDS.Max} />
                      <span className="radioButton"> </span>
                    </label>
                    <label className="radiocontainer descriptionText">
                      {valueStrings.no}
                      <input className="radioLabel" type="radio" defaultChecked={!system.Max} value="false" name={SYSTEM_FIELDS.Max} />
                      <span className="radioButton"> </span>
                    </label>
                  </div>
                </div>
                <div className="column text-right ">
                  <label>{userProfileStrings.enabled}</label>
                  <div onChange={handleRadioChange}>
                    <label className="radiocontainer descriptionText">
                      {valueStrings.yes}
                      <input className="radioLabel" type="radio" defaultChecked={system.Enabled} value="true" name={SYSTEM_FIELDS.Enabled} />
                      <span className="radioButton"> </span>
                    </label>
                    <label className="radiocontainer descriptionText">
                      {valueStrings.no}
                      <input className="radioLabel" type="radio" defaultChecked={!system.Enabled} value="false" name={SYSTEM_FIELDS.Enabled} />
                      <span className="radioButton"> </span>
                    </label>
                  </div>
                </div>
              </div>
              <InputBox
                customClass="margin-top-120"
                labelText={userProfileStrings.address}
                name={SYSTEM_FIELDS.Address}
                value={system.Address}
                placeholder={valueStrings.type}
                onChange={value => handleInputChange(value, SYSTEM_FIELDS.Address)}
                maxLength={INPUT_ADDRESS_MAXIMUM}
              />
              <InputBox
                customClass="margin-top-25"
                labelText={userProfileStrings.cit3Average}
                name={SYSTEM_FIELDS.CIT3Average}
                validator={TextFieldValidator.NUMBER_TWO_DECIMAL}
                value={system.CIT3_Average_threshold !== undefined ? system.CIT3_Average_threshold : ""}
                placeholder={valueStrings.type}
                onChange={value => handleInputChange(value, SYSTEM_FIELDS.CIT3Average)}
                maxLength={8}
              />
            </div>
          </div>
        </DupontDialog>
      )}
      {baselineDialog && (
        <DupontDialog
          open
          title={userProfileStrings.addDefaultBaselineTitle}
          confirmBtnText={valueStrings.yes}
          cancelBtnText={valueStrings.no}
          onClose={handleCloseBaselineDialog}
          disableBackdropClick
        />
      )}
    </>
  );
}
